import apiService from "./api.js";

export const publicationsDjeService = {
  // Buscar publicações DJE
  async getPublications(params = {}) {
    try {
      const queryParams = new URLSearchParams();

      // Parâmetros básicos
      if (params.limit) queryParams.append("limit", params.limit.toString());
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.searchTerm)
        queryParams.append("searchTerm", params.searchTerm);

      // Parâmetros de ordenação
      if (params.sortBy) queryParams.append("sortBy", params.sortBy);
      if (params.sortOrder) queryParams.append("sortOrder", params.sortOrder);

      // Parâmetros de filtro
      if (params.classification)
        queryParams.append("classification", params.classification);
      if (params.tribunal) queryParams.append("tribunal", params.tribunal);
      if (params.tipoDocumento)
        queryParams.append("tipoDocumento", params.tipoDocumento);

      // Filtro por faixa de score
      if (params.scoreRange && params.scoreRange.trim() !== "") {
        queryParams.append("scoreRange", params.scoreRange);
      }

      // Filtros por justiça e estado
      if (params.dailySphere && params.dailySphere.trim() !== "") {
        queryParams.append("dailySphere", params.dailySphere);
      }
      if (params.dailyState && params.dailyState.trim() !== "") {
        queryParams.append("dailyState", params.dailyState);
      }
      if (params.poloPassivo && params.poloPassivo.trim() !== "") {
        queryParams.append("poloPassivo", params.poloPassivo.trim());
      }

      // Filtros por data
      if (params.dateStart && params.dateStart.trim() !== "") {
        queryParams.append("dateStart", params.dateStart);
      }
      if (params.dateEnd && params.dateEnd.trim() !== "") {
        queryParams.append("dateEnd", params.dateEnd);
      }

      // Filtros de arquivamento
      if (params.includeArchived !== undefined) {
        queryParams.append(
          "includeArchived",
          params.includeArchived.toString()
        );
      }
      if (params.newsPublications !== undefined) {
        queryParams.append(
          "newsPublications",
          params.newsPublications.toString()
        );
      }
      if (params.onlyArchived !== undefined) {
        queryParams.append("onlyArchived", params.onlyArchived.toString());
      }
      if (params.onlyDiscarded !== undefined) {
        queryParams.append("onlyDiscarded", params.onlyDiscarded.toString());
      }

      const url = `/publications-dje${
        queryParams.toString() ? "?" + queryParams.toString() : ""
      }`;
      const response = await apiService.get(url);
      return response;
    } catch (error) {
      // Error handled by API service
      throw error;
    }
  },

  // Buscar publicação DJE por ID
  async getPublicationById(id) {
    try {
      const response = await apiService.get(`/publications-dje/${id}`);

      return response;
    } catch (error) {
      // Error handled by API service
      throw error;
    }
  },

  // Marcar publicação como lida
  async markPublicationAsRead(publicationId) {
    try {
      const response = await apiService.put(
          `/publications-dje/${publicationId}/read`,
          {}
      );
      return response;
    } catch (error) {
      // Error handled by API service
      throw error;
    }
  },

  // Estatísticas de publicações DJE
  async getPublicationsStats() {
    try {
      const response = await apiService.get("/publications-dje/stats");
      return response;
    } catch (error) {
      // Error handled by API service
      throw error;
    }
  },

  // Buscar classificações disponíveis
  async getPublicationsClassifications() {
    try {
      const response = await apiService.get(
        "/publications-dje/classifications"
      );
      return response;
    } catch (error) {
      // Error handled by API service
      throw error;
    }
  },

  async suggestPassivePoles(term, limit = 8) {
    const queryParams = new URLSearchParams();
    queryParams.append("q", term);
    queryParams.append("limit", String(limit));
    return apiService.get(
      `/publications-dje/passive-poles?${queryParams.toString()}`,
    );
  },

  // Buscar tribunais disponíveis
  async getTribunals() {
    try {
      const response = await apiService.get("/publications-dje/tribunals");
      return response;
    } catch (error) {
      // Error handled by API service
      throw error;
    }
  },

  // Buscar tipos de documento disponíveis
  async getDocumentTypes() {
    try {
      const response = await apiService.get("/publications-dje/document-types");
      return response;
    } catch (error) {
      // Error handled by API service
      throw error;
    }
  },

  // Buscar esferas de justiça disponíveis
  async getJusticeSpheres() {
    try {
      const response = await apiService.get(
        "/publications-dje/justice-spheres"
      );
      return response;
    } catch (error) {
      // Error handled by API service
      throw error;
    }
  },

  // Buscar estados disponíveis
  async getStates() {
    try {
      const response = await apiService.get("/publications-dje/states");
      return response;
    } catch (error) {
      // Error handled by API service
      throw error;
    }
  },

  // Gerar resumo de IA para uma publicação
  async generateAISummary(publicationDjeId) {
    try {
      const response = await apiService.post(`/publications-dje/${publicationDjeId}/generate-ai-summary`, {});
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Reclassificar publicação DJE individualmente (admin)
  async reclassifyPublication(publicationId) {
    try {
      const response = await apiService.post(
        `/publications-dje/${publicationId}/reclassify`,
        {},
      );
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Arquivar publicação individual (e demais comunicações do mesmo processo)
  async archivePublication(publicationId, razao = "Geral") {
    try {
      const response = await apiService.put(
        `/publications-dje/${publicationId}/archive`,
        { razao },
      );
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Desarquivar publicação individual
  async unarchivePublication(publicationId) {
    try {
      const response = await apiService.put(
        `/publications-dje/${publicationId}/unarchive`,
        {},
      );
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Descartar publicação: arquiva o processo e bloqueia futuras sincronizações
  async discardPublication(publicationId, motivo) {
    try {
      const response = await apiService.put(
        `/publications-dje/${publicationId}/discard`,
        motivo ? { motivo } : {},
      );
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Arquivar publicações em lote
  async bulkArchive(ids) {
    try {
      const response = await apiService.post("/publications-dje/archive/bulk", {
        ids,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Desarquivar publicações em lote
  async bulkUnarchive(ids) {
    try {
      const response = await apiService.post(
        "/publications-dje/unarchive/bulk",
        { ids },
      );
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Descartar publicações em lote
  async bulkDiscard(ids, motivo) {
    try {
      const response = await apiService.post("/publications-dje/discard/bulk", {
        ids,
        ...(motivo ? { motivo } : {}),
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Reabilitar publicação descartada
  async restorePublication(publicationId) {
    try {
      const response = await apiService.put(
        `/publications-dje/${publicationId}/restore`,
        {},
      );
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Reabilitar publicações descartadas em lote
  async bulkRestore(ids) {
    try {
      const response = await apiService.post("/publications-dje/restore/bulk", {
        ids,
      });
      return response;
    } catch (error) {
      throw error;
    }
  },
};
